import React, { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  Box, Typography, Paper, Stack, Switch,
  List, ListItem, ListItemText, ListItemIcon,
  CircularProgress, Fab
} from "@mui/material";
import {
  Assignment, BarChart, Favorite, Bloodtype,
  AirlineSeatReclineNormal, Thermostat, Timer,
  WaterDrop, Fastfood, Bed, Nightlight,
  CheckCircle, Shield, HealthAndSafety, Vaccines, Save
} from "@mui/icons-material";
import { getClientObservations, updateClientObservations } from "../../../../../api/services/Client/careObservation";
import useClientDetails from "../../../../../hooks/useClientDetails";
import { QK } from "../../../../../base/qk";

const CarePlanConfiguration = () => {
  const { id: clientId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [obsSettings, setObsSettings] = useState({});
  const { fullName } = useClientDetails(clientId);

  const bottomRef = useRef(null);

  const { isLoading } = useQuery(
    [QK.careObservations, clientId],
    () => getClientObservations(clientId),
    {
      enabled: !!clientId,
      onSuccess: (data) => {
        setObsSettings(data);
        setTimeout(() => {
          bottomRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "end"
          });
        }, 600);
      },
      onError: () => {
        enqueueSnackbar("Error loading settings", { variant: "error" });
      }
    }
  );

  const mutation = useMutation(
    (updatedData) => updateClientObservations(clientId, updatedData),
    {
      onSuccess: () => {
        enqueueSnackbar("Configuration saved successfully!", { variant: "success" });
        queryClient.invalidateQueries([QK.careObservations, clientId]);
      },
      onError: () => {
        enqueueSnackbar("Failed to save changes", { variant: "error" });
      }
    }
  );

  const handleToggle = (key) => {
    setObsSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    mutation.mutate(obsSettings);
  };

  const clinicalItems = [
    { key: "abc_patterns", label: "ABC Patterns", icon: <BarChart /> },
    { key: "blood_pressure", label: "Blood Pressure", icon: <Favorite /> },
    { key: "blood_sugar", label: "Blood Sugar", icon: <Bloodtype /> },
    { key: "bowel_movement", label: "Bowel Movement", icon: <HealthAndSafety /> },
    { key: "fluid", label: "Fluid", icon: <WaterDrop /> },
    { key: "meal", label: "Meal", icon: <Fastfood /> },
    { key: "news2", label: "NEWS2", icon: <Vaccines /> },
    { key: "oxygen_saturation", label: "Oxygen Saturation", icon: <Timer /> },
    { key: "pulse", label: "Pulse", icon: <Favorite /> },
    { key: "repositioning", label: "Repositioning", icon: <AirlineSeatReclineNormal /> },
    { key: "respiration", label: "Respiration", icon: <Timer /> },
    { key: "seizure", label: "Seizure", icon: <HealthAndSafety /> },
    { key: "temperature", label: "Temperature", icon: <Thermostat /> },
    { key: "urinalysis", label: "Urinalysis", icon: <WaterDrop /> },
    { key: "weight", label: "Weight", icon: <BarChart /> },
  ];

  const careItems = [
    { key: "continence", label: "Continence", icon: <WaterDrop color="primary" /> },
    { key: "final_days", label: "Final Days", icon: <HealthAndSafety color="secondary" /> },
    { key: "mouth_care", label: "Mouth Care", icon: <CheckCircle color="info" /> },
    { key: "night_duties", label: "Night Duties", icon: <Nightlight color="primary" /> },
    { key: "personal_care", label: "Personal Care", icon: <Favorite color="error" /> },
    { key: "sskin_bundle", label: "Pressure Ulcer prevention (SSKIN Bundle)", icon: <Shield color="primary" /> },
    { key: "sleeping_night", label: "Sleeping and Night", icon: <Bed color="primary" /> },
    { key: "meal_secondary", label: "Meal", icon: <Fastfood color="warning" /> },
    { key: "home_first", label: "Home First Checklist", icon: <Assignment color="info" /> },
  ];

  const RenderToggleList = ({ items }) => (
    <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
      {items.map((item) => (
        <ListItem
          key={item.key}
          divider
          secondaryAction={
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="caption" fontWeight="bold" color={obsSettings[item.key] ? "error.main" : "text.disabled"}>
                {obsSettings[item.key] ? "YES" : "NO"}
              </Typography>
              <Switch
                edge="end"
                checked={!!obsSettings[item.key]}
                onChange={() => handleToggle(item.key)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#f44336' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#f44336' },
                }}
              />
            </Stack>
          }
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          <ListItemText primary={<Typography variant="body2">{item.label}</Typography>} />
        </ListItem>
      ))}
    </List>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 0 }, bgcolor: "#f4f6f8", position: "relative", minHeight: '100vh' }}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ddd', pb: 1 }}>
          <Stack direction="row" spacing={4}>
            <Typography sx={{ borderBottom: '3px solid #1976d2', fontWeight: 'bold', pb: 1, color: '#1976d2', fontSize: '14px' }}>
              {`${fullName}'s Observations`}
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ mb: 4, bgcolor: '#fff9f9', p: 2, borderRadius: 1 }}>
          {["Notes", "Consent Form", "Body Map", "Communication Notes"].map((text) => (
            <Box key={text} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f0f0f0' }}>
              <Typography variant="body2" color="text.secondary">{text}</Typography>
              <Typography variant="body2" fontWeight="bold" color="error.main">YES</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '280px' }}>
            <Typography variant="overline" color="primary" fontWeight="bold">Clinical Observations</Typography>
            <RenderToggleList items={clinicalItems} />
          </Box>
          <Box sx={{ flex: '1 1 300px', minWidth: '280px' }}>
            <Typography variant="overline" color="primary" fontWeight="bold">Care & Checklists</Typography>
            <RenderToggleList items={careItems} />
          </Box>
        </Box>

        <div ref={bottomRef} style={{ height: "80px" }} />
      </Paper>

      <Fab
        variant="extended"
        color="primary"
        onClick={handleSave}
        disabled={mutation.isLoading}
        sx={{
          position: 'fixed',
          bottom: 40,
          right: 40,
          px: 3,
          py: 1.5,
          textTransform: 'none',
          fontWeight: 'bold',
          fontSize: '15px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          '&:hover': { transform: 'scale(1.05)', transition: '0.2s' }
        }}
      >
        {mutation.isLoading ? (
          <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
        ) : (
          <Save sx={{ mr: 1 }} />
        )}
        {mutation.isLoading ? "Saving..." : "Save Configuration"}
      </Fab>
    </Box>
  );
};

export default CarePlanConfiguration;